import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@hydrogen/elements.core.i18n';
import {
    Heading, Button, TextInput, ErrorMessage, Preloader,
} from '@dekafvv/sharedfrontend';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { useForm } from 'react-hook-form';
import { authenticateUser } from 'redux/auth/authActions';
import { login } from 'services/authenticate';
import { ForgotPassword } from 'components/ForgotPasswordWizard/ForgotPasswordWizard';
import DekaLogo from 'widgets/Header/ui/DekaLogo';
import { password, email, loginSchema } from './data';
import styles from './Login.module.css';

type TextInputError = {
    message: string,
}

const Login = () => {
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isForgotPasswordVisible, setIsForgotPasswordVisible] = useState<boolean>(false);
    const [wrongPasswordCounter, setWrongPasswordCounter] = useState<number>(1);
    const [showForgotPassword, setShowForgotPassword] = useState<boolean>(true);
    const { t } = useLanguage();
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const {
        handleSubmit, formState: { errors }, setValue, watch, trigger,
    } = useForm({
        resolver: useYupValidationResolver(loginSchema(t)),
        shouldUnregister: false,
    });
    const onChange = (type) => async (e) => {
        setValue(type, e.target?.value ?? e);
        await trigger(type);
    };

    const body = {
        userName: watch(email.name),
        password: watch(password.name),
    };

    const authenticateUserMutation = useMutation({
        mutationFn: login,
        onSuccess(data: any) {
            setWrongPasswordCounter(1);
            dispatch(authenticateUser({
                contactId: data.data.contactId,
                jwtAccessToken: data.data.jwtAccessToken,
                expiryDate: data.data.expiryDate,
                expiryInSeconds: data.data.expiryInSeconds,
            }));
            navigate('/');
            setIsLoading(false);
        },
        onError(error: any) {
            if (wrongPasswordCounter < 3 && error.response.data.message !== 'ContactLocked') {
                setErrorMessage(t('error.password.incorrect-password'));
            } else if (wrongPasswordCounter === 3 && error.response.data.message !== 'ContactLocked') {
                setErrorMessage(t('error.password.incorrect-password-last-chance'));
            } else if (error.response.data.message === 'ContactLocked') {
                setShowForgotPassword(false);
                setErrorMessage(t('error.password.incorrect-password.softlock'));
            } else {
                setErrorMessage(t('error.password.incorrect-password'));
            }
            setWrongPasswordCounter(wrongPasswordCounter + 1);
            setIsLoading(false);
        },
    });

    const handleLogin = () => {
        setIsLoading(true);
        authenticateUserMutation.mutate({ ...body });
    };

    const handleForgotPassword = () => {
        setIsForgotPasswordVisible(true);
    };

    return (
        <Preloader isLoading={isLoading} error={null}>
            <div className={styles.centeredContainer}>
                <div className={styles.backdrop} />
                <div className={styles['inner-container']}>
                    <div className={styles.header}>
                        <DekaLogo />
                    </div>
                    <div
                        className={styles['login-container']}
                    >
                        <ForgotPassword
                            visible={isForgotPasswordVisible}
                            onClose={() => setIsForgotPasswordVisible(false)}
                        />
                        <Heading size={2} textAlign="left">{t('login.header')}</Heading>
                        <TextInput
                            {...email}
                            onChange={onChange(email.name)}
                            label={t('email.field')}
                            error={errors[email.name] as TextInputError}
                            value={watch(email.name) || ''}
                            className="mt-6"
                        />
                        <TextInput
                            {...password}
                            onChange={onChange(password.name)}
                            label={t('password.field')}
                            error={errors[password.name] as TextInputError}
                            value={watch(password.name) || ''}
                            className="mt-6"
                        />
                        {showForgotPassword && (
                            <div className="text-center">
                                <Button
                                    onClick={handleForgotPassword}
                                    className={styles['forgot-password-link']}
                                    buttonType="link"
                                    size="small"
                                >
                                    {t('button.forgotten-password')}
                                </Button>
                            </div>
                        )}
                        <ErrorMessage className="mt-6 mb-6">{errorMessage}</ErrorMessage>
                        <div className="center">
                            <Button
                                onClick={handleSubmit(() => handleLogin())}
                            >
                                {t('button.login')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Preloader>
    );
};

export default Login;
